import React, { useState, useEffect, useRef } from 'react';

import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './ConditionModal.css';

const ConditionModal = ({ onClose, onSubmit, condition, mode }) => {
  const initialFormState = {
    name: '',
    description: '',
    imageUrl: '', // image path
    imagePublicId: '',
    tags: [],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null); // raw file
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { name, description, imageUrl, tags } = formData;

  const firstInputRef = useRef();

  useEffect(() => {
    setFormData({
      name: condition?.name || '',
      description: condition?.description || '',
      imageUrl: condition?.imageUrl || '',
      imagePublicId: condition?.imagePublicId || '',
      tags: condition?.tags || [],
    });
    setSelectedFile(null);
    setImagePreviewUrl(condition?.imageUrl || '');

    firstInputRef.current?.focus();
  }, [mode, condition]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setSelectedFile(null);
    setImagePreviewUrl(null);

    setFormData((prev) => ({
      ...prev,
      imageUrl: '',
      imagePublicId: '',
    }));
  };

  const handleTagsChange = (e) => {
    const tagsArray = e.target.value.split(',').map((t) => t.trim());

    setFormData((prev) => ({
      ...prev,
      tags: tagsArray,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setProgress(0);

    try {
      let uploadedData = null;

      if (selectedFile) {
        uploadedData = await uploadImageToCloudinaryWithProgress(
          selectedFile,
          setProgress,
          'conditions',
          [0, 70],
        );
      }

      const finalData = {
        ...formData,
        imageUrl: uploadedData?.url || formData.imageUrl || '',
        imagePublicId: uploadedData?.publicId || formData.imagePublicId || '',
      };

      setProgress(100);

      await onSubmit(finalData);
    } catch (err) {
      console.error('Submit failed:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <EditModalShell
      formId='condition-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      itemLabel='Condition'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}
      <ImageInputWithPreview
        imagePreviewUrl={imagePreviewUrl}
        imageUrl={imageUrl}
        currentImgAlt='Current Condition'
        onFileChange={handleFileChange}
        onRemoveImage={handleRemoveImage}
      />
      <FormField
        type='text'
        name='name'
        label='Name'
        value={name}
        onChange={handleInputChange}
        ref={firstInputRef}
        required
      />
      <FormField
        type='textarea'
        name='description'
        label='Description'
        value={description}
        onChange={handleInputChange}
        required
      />
      <FormField
        type='text'
        name='tags'
        label='Tags'
        value={tags}
        onChange={handleTagsChange}
      />
    </EditModalShell>
  );
};

export default ConditionModal;
